import React from 'react';
import { find, forEach, isEmpty, isPlainObject } from 'lodash';
import cronstrue from 'cronstrue';
import { Link } from 'react-router-dom';
import { cronToHumanReadable } from 'new/utils/cronDisplay';
import Constants from '../../../../Constants';
import { getDateString, getLocalTimeZoneLabel } from 'js/lib/DateUtils';
import UIUtils from '../../../../lib/UIUtils';
import { ROUTE_CREDENTIALS } from 'js/constants/pathnames';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vs } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { isSqlQueryField } from 'js/components/elements/configViews/ConfigurationFields.utils';

const SQL_HIGHLIGHTER_STYLE: React.CSSProperties = {
  margin: 0,
  fontSize: '14px',
  maxHeight: '400px',
  overflow: 'auto',
  backgroundColor: 'var(--color-galactic-sand-bg)',
  border: '1px solid var(--color-outline)',
  borderRadius: '4px',
  padding: '12px',
};

type ConfigFieldValueProps = {
  field: any;
  value: any;
};

export default function ConfigFieldValue({ field, value }: ConfigFieldValueProps) {
  if (!field || value === undefined || value === null) {
    return '(Empty)';
  }

  switch (field.name) {
    case 'iteration.type':
      const iterationTypeOption = find(Constants.IterationTypes, { value });
      return iterationTypeOption ? iterationTypeOption.label : 'Default';

    case 'response.format':
      switch (value) {
        case 'xml':
          return 'XML';

        case 'json':
          return 'JSON';

        default:
          return value;
      }

    case 'mapping':
      return `${value}`;

    default:
  }

  switch (field.type) {
    case 'boolean':
      return value ? 'Yes' : 'No';

    case 'frequency':
      if (value === '0 0/1 * 1/1 * ? *') {
        return 'Continuously';
      }

      const cronOptions = UIUtils.parseCron(value);
      const { interval, timestamp } = cronOptions;

      if (!interval) {
        return cronToHumanReadable(value) + ` (${getLocalTimeZoneLabel()})`;
      }

      if (interval === 'Once') {
        return 'Once on ' + getDateString(timestamp, 'MMM D, YYYY [at] h:mma z');
      }

      if (interval === '15 Minutes' || interval === '30 Minutes') {
        return `Every ${interval}, starting at ${
          cronOptions.mm
        } minutes past the hour (${getLocalTimeZoneLabel()})`;
      }

      // workaround for cronstrue weekday range of 0-6 instead of 1-7
      const adjustedCronOptions = { ...cronOptions };

      if (interval === 'Week') {
        (adjustedCronOptions as any).dayOfWeek -= 1;
      }

      return (
        cronstrue.toString(UIUtils.getCron(adjustedCronOptions, false)) +
        ` (${getLocalTimeZoneLabel()})`
      );

    case 'datetime':
      return getDateString(value);

    case 'object':
      let result: string[] = [];

      forEach(value, (paramValue, paramName) => {
        result.push(`${paramName}: ${paramValue}`);
      });

      return result.join(', ');

    case 'script_credential':
      if (isPlainObject(value)) {
        return <Link to={`${ROUTE_CREDENTIALS}/${value.id}`}>{value.name}</Link>;
      }

      return value;

    default:
  }

  if (isSqlQueryField(field) && typeof value === 'string' && value.length > 0) {
    return (
      <div style={{ width: '100%' }}>
        <SyntaxHighlighter language="sql" style={vs} customStyle={SQL_HIGHLIGHTER_STYLE}>
          {value}
        </SyntaxHighlighter>
      </div>
    );
  }

  if (!isEmpty(field.options)) {
    if (typeof value === 'object') {
      return '(Invalid value)';
    }

    const selectedOption = find(field.options, { value });

    if (selectedOption) {
      return selectedOption.label;
    }
  }

  return value;
}
